// angular
import {Component, OnInit, effect, inject} from "@angular/core";
import {NgClass} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {NavigationEnd, Router} from "@angular/router";
import {MatIcon} from "@angular/material/icon";
// services
import {ScreenSizeService} from "@app/shared/services/screen-size.service";
import {LayoutService} from "../shared/services/layout.service";
import {RouteService} from "@app/shared/services/route.service";
// types
import {ScreenSizeType} from "@app/shared/types/screen-size.type";

@Component({
  selector: "app-page-header",
  templateUrl: "./page-header.component.html",
  styleUrls: ["./page-header.component.scss"],
  standalone: true,
  imports: [NgClass, TranslateModule, MatIcon],
})
export class PageHeaderComponent implements OnInit {
  public screenSize: ScreenSizeType = "unknown";
  public menuIsOpen?: boolean;
  public pageTitle!: string;
  public showAddButton = false;
  public canGoBack = false;

  // dependencies
  private _screenSizeService = inject(ScreenSizeService);
  private _layoutService = inject(LayoutService);
  private _router = inject(Router);
  private _routeService = inject(RouteService);

  constructor() {
    effect(() => {
      this.pageTitle = this._layoutService.pageTitle();
    });
  }

  ngOnInit() {
    // set initial state based on current URL (for page refresh)
    this.updateBackButtonVisibility(this._router.url);

    this._screenSizeService.screenSize$.subscribe(
      (size) => (this.screenSize = size)
    );
    this._layoutService.menuIsOpen$.subscribe((isOpen) => {
      this.menuIsOpen = isOpen;
    });

    // for the first render
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const destinationUrl = event.url.split("?")[0];
        const prevUrl = this._routeService.getPreviousUrl()?.split("?")[0];

        // update back button visibility on navigation
        this.updateBackButtonVisibility(event.url);

        if (!prevUrl || destinationUrl !== prevUrl) {
          // before each navigation, if url changes and not query params,
          // the old payloads (related to the old route)
          // will be removed and the new payloads will be added by the page-header payload directive
          const payloadBox = document.querySelector("#page-header-payload-box");
          if (payloadBox) {
            payloadBox.innerHTML = "";
          }
        }
      }
    });
  }

  private updateBackButtonVisibility(url: string): void {
    const urlWithoutQuery = url.split("?")[0];
    this.canGoBack = this.shouldShowBackButton(urlWithoutQuery);
  }

  private shouldShowBackButton(url: string): boolean {
    // remove query parameters
    const urlWithoutQuery = url.split("?")[0];

    // split the URL into segments
    const segments = urlWithoutQuery.split("/").filter((segment) => segment);

    // if more than 2 levels, always show back button
    // e.g., /customers/view/wallet
    if (segments.length > 2) {
      return true;
    }

    // if exactly 2 levels, check if second segment is id
    // e.g., /orders/f9c45c97......
    if (segments.length === 2) {
      return this.isId(segments[1]);
    }

    // otherwise, don't show back button
    return false;
  }

  private isId(segment: string): boolean {
    // UUID pattern
    const uuidPattern =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    // numeric ID pattern
    const numericIdPattern = /^\d+$/;
    // long alphanumeric pattern
    const longAlphanumericPattern = /^[a-zA-Z0-9]{20,}$/;

    return (
      uuidPattern.test(segment) ||
      numericIdPattern.test(segment) ||
      longAlphanumericPattern.test(segment)
    );
  }

  private getFirstLevelRoute(url: string): string {
    // remove query parameters
    const urlWithoutQuery = url.split("?")[0];

    // split the URL into segments
    const segments = urlWithoutQuery.split("/").filter((segment) => segment);

    // return only the first segment
    return "/" + segments[0];
  }

  private getParentRoute(url: string): string {
    // remove query parameters
    const urlWithoutQuery = url.split("?")[0];

    // split the URL into segments
    const segments = urlWithoutQuery.split("/").filter((segment) => segment);

    // remove the last segment to get parent route
    segments.pop();

    // return the parent route
    return "/" + segments.join("/");
  }

  public goBack(): void {
    const currentUrl = this._router.url;
    const urlWithoutQuery = currentUrl.split("?")[0];
    const segments = urlWithoutQuery.split("/").filter((segment) => segment);
    const currentQueryParams = this._router.parseUrl(currentUrl).queryParams;

    // if route has exactly 3 levels and the 3rd level is not id
    // navigate to the first level route
    if (segments.length === 3 && !this.isId(segments[2])) {
      const firstLevelRoute = this.getFirstLevelRoute(currentUrl);
      this._router.navigate([firstLevelRoute], {
        queryParams: currentQueryParams,
      });
      return;
    }

    // otherwise, try to use previous URL from route service
    const prevUrl = this._routeService.getPreviousUrl();

    if (prevUrl) {
      // previous URL exists, navigate to it with filters/sorts intact
      this._router.navigateByUrl(prevUrl);
    } else {
      // if no previous URL (e.g., page refresh), fallback to parent route
      const parentRoute = this.getParentRoute(currentUrl);
      this._router.navigate([parentRoute], {
        queryParams: currentQueryParams,
      });
    }
  }
}
